<template>
  <div class="trips-page">
    <!-- Background Image -->
    <div class="page-background"></div>
    <!-- Page Header -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <div>
        <h1 class="h3 mb-1">Manage Trips</h1>
        <p class="text-muted mb-0">Create and manage travel packages</p>
      </div>
      <router-link to="/admin/trips/create" class="btn btn-primary">
        <i class="fas fa-plus me-2"></i>
        Add New Trip
      </router-link>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="loading text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="mt-3 text-muted">Loading trips...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="alert alert-danger" role="alert">
      <i class="fas fa-exclamation-triangle me-2"></i>
      {{ error }}
      <button @click="loadTrips" class="btn btn-sm btn-outline-danger ms-2">
        <i class="fas fa-redo me-1"></i>
        Retry
      </button>
    </div>

    <!-- Empty State -->
    <div v-else-if="allTrips.length === 0" class="text-center py-5">
      <i class="fas fa-map-marked-alt fa-4x text-muted mb-4"></i>
      <h4>No trips found</h4>
      <p class="text-muted mb-4">Start by creating your first travel package</p>
      <router-link to="/admin/trips/create" class="btn btn-primary">
        <i class="fas fa-plus me-2"></i>
        Create First Trip
      </router-link>
    </div>

    <!-- Trips Grid -->
    <div v-else class="row">
      <div
        v-for="trip in sortedTrips"
        :key="trip.id"
        class="col-lg-6 col-md-6 mb-4"
      >
        <div class="card trip-card h-100">
          <!-- Trip Image -->
          <div class="trip-image-container">
            <img
              v-if="trip.images && trip.images.length > 0"
              :src="trip.images[0]"
              :alt="trip.place"
              class="card-img-top trip-image"
            />
            <div v-else class="card-img-top trip-image-placeholder">
              <i class="fas fa-image fa-3x text-muted"></i>
            </div>

            <!-- Image Count Badge -->
            <div
              v-if="trip.images && trip.images.length > 1"
              class="image-count-badge"
            >
              <i class="fas fa-images me-1"></i>
              {{ trip.images.length }}
            </div>

            <!-- Video Badge -->
            <div v-if="trip.video" class="video-badge">
              <i class="fas fa-play"></i>
            </div>
          </div>

          <div class="card-body d-flex flex-column">
            <!-- Trip Info -->
            <div class="flex-grow-1">
              <h5 class="card-title mb-2">{{ trip.place }}</h5>

              <div class="trip-meta mb-3">
                <div class="d-flex align-items-center mb-2">
                  <i class="fas fa-clock text-muted me-2"></i>
                  <span>{{ trip.days }} days</span>
                </div>
                <div class="d-flex align-items-center mb-2">
                  <i class="fas fa-calendar-alt text-muted me-2"></i>
                  <span>{{ formatDate(trip.date) }}</span>
                </div>
                <div class="d-flex align-items-center mb-2">
                  <i class="fas fa-rupee-sign text-muted me-2"></i>
                  <span class="fw-bold text-primary"
                    >&#8377;{{ trip.price }}</span
                  >
                </div>
              </div>

              <p class="card-text text-truncate-3">{{ trip.description }}</p>
            </div>

            <!-- Trip Actions -->
            <div class="trip-actions mt-3">
              <div class="btn-group w-100" role="group">
                <router-link
                  :to="`/admin/trips/${trip.id}/edit`"
                  class="btn btn-outline-primary btn-sm"
                >
                  <i class="fas fa-edit me-1"></i>
                  Edit
                </router-link>

                <button
                  @click="viewTrip(trip)"
                  class="btn btn-outline-info btn-sm"
                >
                  <i class="fas fa-eye me-1"></i>
                  View
                </button>

                <button
                  @click="deleteTrip(trip)"
                  class="btn btn-outline-danger btn-sm"
                >
                  <i class="fas fa-trash me-1"></i>
                  Delete
                </button>
              </div>
            </div>

            <!-- Additional Info -->
            <div class="trip-extras mt-2">
              <div class="d-flex justify-content-between align-items-center">
                <small class="text-muted">
                  Created: {{ formatDate(trip.createdAt) }}
                </small>
                <div class="trip-badges">
                  <span v-if="trip.itineraryPdf" class="badge bg-info me-1">
                    <i class="fas fa-file-pdf me-1"></i>
                    PDF
                  </span>
                  <span v-if="trip.googleSheetUrl" class="badge bg-success">
                    <i class="fas fa-table me-1"></i>
                    Sheet
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Trip Details Modal -->
    <TripDetailsModal
      v-if="selectedTrip"
      :trip="selectedTrip"
      @close="selectedTrip = null"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import moment from "moment";
import Swal from "sweetalert2";
import TripDetailsModal from "@/components/TripDetailsModal.vue";

export default {
  name: "Trips",
  components: {
    TripDetailsModal,
  },
  data() {
    return {
      selectedTrip: null,
    };
  },
  computed: {
    ...mapGetters("trips", ["allTrips", "isLoading", "error"]),
    sortedTrips() {
      // Sort trips by updatedAt (newest first)
      return [...this.allTrips].sort((a, b) => {
        const dateA = new Date(a.updatedAt || a.createdAt || 0);
        const dateB = new Date(b.updatedAt || b.createdAt || 0);
        return dateB - dateA; // Descending order (newest first)
      });
    }
  },
  methods: {
    ...mapActions("trips", ["fetchTrips", "deleteTrip"]),

    formatDate(dateString) {
      return moment(dateString).format("MMM DD, YYYY");
    },

    async loadTrips() {
      try {
        await this.fetchTrips();
      } catch (error) {
        console.error("Error loading trips:", error);
      }
    },

    viewTrip(trip) {
      this.selectedTrip = trip;
    },

    async deleteTrip(trip) {
      try {
        const result = await Swal.fire({
          title: "Delete Trip",
          text: `Are you sure you want to delete "${trip.place}"? This action cannot be undone.`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#dc3545",
          cancelButtonColor: "#6c757d",
          confirmButtonText: "Yes, delete it!",
          cancelButtonText: "Cancel",
        });

        if (result.isConfirmed) {
          await this.$store.dispatch("trips/deleteTrip", trip.id);

          Swal.fire({
            title: "Deleted!",
            text: "Trip has been deleted successfully.",
            icon: "success",
            timer: 3000,
            showConfirmButton: false,
          });
        }
      } catch (error) {
        Swal.fire({
          title: "Error",
          text: error.message,
          icon: "error",
          confirmButtonColor: "#2c5aa0",
        });
      }
    },
  },
  async created() {
    await this.loadTrips();
  },
};
</script>

<style scoped>
.trips-page {
  animation: fadeIn 0.5s ease-out;
  position: relative;
  min-height: 100vh;
}

.page-background {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url("@/assets/travel-concept-with-lugagge-hat.jpg");
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  opacity: 0.1;
  z-index: -1;
}

.trip-card {
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  border: 1px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12), 0 4px 15px rgba(0, 0, 0, 0.08);
  border-radius: 25px;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  backdrop-filter: blur(10px);
  position: relative;
  overflow: visible;
  padding: 6px;
  width: 100%;
  min-width: 320px;
}

.trip-card::after {
  content: "";
  position: absolute;
  top: 6px;
  left: 6px;
  right: 6px;
  bottom: 6px;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-radius: 19px;
  box-shadow: inset 0 2px 8px rgba(0, 0, 0, 0.05), 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.6);
  z-index: 1;
}

.trip-card::before {
  content: "";
  position: absolute;
  top: 2px;
  left: 2px;
  right: 2px;
  bottom: 2px;
  background: linear-gradient(
    135deg,
    rgba(44, 90, 160, 0.15),
    rgba(248, 183, 57, 0.15),
    rgba(44, 90, 160, 0.1)
  );
  border-radius: 23px;
  opacity: 0;
  transition: opacity 0.4s ease;
  z-index: 2;
}

.trip-card:hover::before {
  opacity: 1;
}

.trip-card:hover {
  transform: translateY(-12px) scale(1.02);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.18), 0 8px 25px rgba(44, 90, 160, 0.1);
  border-color: rgba(44, 90, 160, 0.3);
}

.trip-image-container {
  position: relative;
  overflow: hidden;
  border-radius: 19px 19px 0 0;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-bottom: none;
  z-index: 3;
}

.trip-image {
  height: 200px;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.trip-card:hover .trip-image {
  transform: scale(1.05);
}

.trip-image-placeholder {
  height: 200px;
  background: var(--light-color);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 19px 19px 0 0;
}

.image-count-badge {
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
}

.card-body {
  border-radius: 0 0 19px 19px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-top: none;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  z-index: 3;
  position: relative;
}

.video-badge {
  position: absolute;
  bottom: 10px;
  right: 10px;
  background: rgba(255, 255, 255, 0.9);
  color: var(--primary-color);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
}

.trip-meta {
  font-size: 0.9rem;
}

.text-truncate-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.4;
  max-height: 4.2em;
}

.trip-actions .btn {
  flex: 1;
}

.trip-badges .badge {
  font-size: 0.65rem;
}

@media (max-width: 992px) {
  .trip-card {
    min-width: 280px;
  }
}

@media (max-width: 768px) {
  .trip-card {
    min-width: 85%;
    max-width: 90%;
  }

  .trip-image,
  .trip-image-placeholder {
    height: 250px;
  }

  .trip-actions .btn-group {
    flex-direction: column;
  }

  .trip-actions .btn {
    margin-bottom: 0.25rem;
  }

  .trip-actions .btn:last-child {
    margin-bottom: 0;
  }
}
</style>
